/**
 * Salesforce Formula Evaluator
 * Supports: IF, AND, OR, NOT, ISBLANK, ISNULL, ISPICKVAL, PRIORVALUE,
 *           TEXT, VALUE, LEN, LEFT, RIGHT, MID, CONTAINS, BEGINS, FIND,
 *           LOWER, UPPER, TRIM, SUBSTITUTE, BR, HYPERLINK,
 *           TODAY, NOW, DATEVALUE, YEAR, MONTH, DAY, ADDMONTHS,
 *           ROUND, FLOOR, CEILING, ABS, MOD, MAX, MIN, SQRT, EXP, LOG,
 *           math operators (+,-,*,/,^), comparison (=,!=,<,>,<=,>=),
 *           logical (&& ||), string concat (&), merge fields {!Field}
 */

// ── Tokeniser ────────────────────────────────────────────────────────────────

const TT = {
  NUMBER: 'NUMBER', STRING: 'STRING', BOOL: 'BOOL', NULL: 'NULL',
  IDENT: 'IDENT', LPAREN: 'LPAREN', RPAREN: 'RPAREN',
  COMMA: 'COMMA', DOT: 'DOT', COLON: 'COLON',
  PLUS: 'PLUS', MINUS: 'MINUS', STAR: 'STAR', SLASH: 'SLASH', CARET: 'CARET',
  AMPERSAND: 'AMPERSAND',
  EQ: 'EQ', NEQ: 'NEQ', LT: 'LT', GT: 'GT', LTE: 'LTE', GTE: 'GTE',
  EOF: 'EOF',
};

function tokenize(src) {
  const tokens = [];
  let i = 0;
  while (i < src.length) {
    // skip whitespace
    if (/\s/.test(src[i])) { i++; continue; }
    // merge field {!Field.Sub}
    if (src[i] === '{' && src[i+1] === '!') {
      let j = i + 2;
      while (j < src.length && src[j] !== '}') j++;
      tokens.push({ type: TT.IDENT, value: src.slice(i+2, j), merge: true });
      i = j + 1; continue;
    }
    // string
    if (src[i] === '"') {
      let j = i + 1, s = '';
      while (j < src.length && src[j] !== '"') { s += src[j]; j++; }
      tokens.push({ type: TT.STRING, value: s });
      i = j + 1; continue;
    }
    // number
    if (/[0-9]/.test(src[i]) || (src[i] === '-' && /[0-9]/.test(src[i+1]) && (tokens.length === 0 || ['COMMA','LPAREN','PLUS','MINUS','STAR','SLASH','EQ','NEQ','LT','GT','LTE','GTE'].includes(tokens[tokens.length-1]?.type)))) {
      let j = i, s = '';
      if (src[j] === '-') { s += '-'; j++; }
      while (j < src.length && /[0-9.]/.test(src[j])) { s += src[j]; j++; }
      tokens.push({ type: TT.NUMBER, value: parseFloat(s) });
      i = j; continue;
    }
    // two-char operators
    if (src[i] === '<' && src[i+1] === '>') { tokens.push({ type: TT.NEQ }); i += 2; continue; }
    if (src[i] === '!' && src[i+1] === '=') { tokens.push({ type: TT.NEQ }); i += 2; continue; }
    if (src[i] === '<' && src[i+1] === '=') { tokens.push({ type: TT.LTE }); i += 2; continue; }
    if (src[i] === '>' && src[i+1] === '=') { tokens.push({ type: TT.GTE }); i += 2; continue; }
    // single-char operators
    const single = { '(': TT.LPAREN, ')': TT.RPAREN, ',': TT.COMMA, '.': TT.DOT, ':': TT.COLON, '+': TT.PLUS, '-': TT.MINUS, '*': TT.STAR, '/': TT.SLASH, '^': TT.CARET, '&': TT.AMPERSAND, '<': TT.LT, '>': TT.GT, '=': TT.EQ };
    if (single[src[i]]) { tokens.push({ type: single[src[i]] }); i++; continue; }
    // identifier / keyword
    if (/[a-zA-Z_]/.test(src[i])) {
      let j = i, s = '';
      while (j < src.length && /[a-zA-Z0-9_]/.test(src[j])) { s += src[j]; j++; }
      const up = s.toUpperCase();
      if (up === 'TRUE') tokens.push({ type: TT.BOOL, value: true });
      else if (up === 'FALSE') tokens.push({ type: TT.BOOL, value: false });
      else if (up === 'NULL') tokens.push({ type: TT.NULL, value: null });
      else tokens.push({ type: TT.IDENT, value: s });
      i = j; continue;
    }
    i++; // skip unknown
  }
  tokens.push({ type: TT.EOF });
  return tokens;
}

// ── Parser → AST ─────────────────────────────────────────────────────────────

function parse(tokens) {
  let pos = 0;
  const peek = () => tokens[pos];
  const eat = (type) => {
    const t = tokens[pos];
    if (type && t.type !== type) throw new Error(`Expected ${type} got ${t.type}`);
    pos++;
    return t;
  };

  function parseExpr() { return parseComparison(); }

  function parseComparison() {
    let left = parseAdditive();
    while ([TT.EQ, TT.NEQ, TT.LT, TT.GT, TT.LTE, TT.GTE].includes(peek().type)) {
      const op = eat().type;
      const right = parseAdditive();
      left = { type: 'BinOp', op, left, right };
    }
    return left;
  }

  function parseAdditive() {
    let left = parseMultiplicative();
    while ([TT.PLUS, TT.MINUS, TT.AMPERSAND].includes(peek().type)) {
      const op = eat().type;
      const right = parseMultiplicative();
      left = { type: 'BinOp', op, left, right };
    }
    return left;
  }

  function parseMultiplicative() {
    let left = parsePower();
    while ([TT.STAR, TT.SLASH].includes(peek().type)) {
      const op = eat().type;
      const right = parsePower();
      left = { type: 'BinOp', op, left, right };
    }
    return left;
  }

  function parsePower() {
    let left = parseUnary();
    if (peek().type === TT.CARET) { eat(); return { type: 'BinOp', op: TT.CARET, left, right: parsePower() }; }
    return left;
  }

  function parseUnary() {
    if (peek().type === TT.MINUS) { eat(); return { type: 'Unary', op: '-', expr: parsePrimary() }; }
    return parsePrimary();
  }

  function parsePrimary() {
    const t = peek();
    if (t.type === TT.NUMBER) { eat(); return { type: 'Literal', value: t.value }; }
    if (t.type === TT.STRING) { eat(); return { type: 'Literal', value: t.value }; }
    if (t.type === TT.BOOL)   { eat(); return { type: 'Literal', value: t.value }; }
    if (t.type === TT.NULL)   { eat(); return { type: 'Literal', value: null }; }
    if (t.type === TT.LPAREN) {
      eat();
      const expr = parseExpr();
      eat(TT.RPAREN);
      return expr;
    }
    if (t.type === TT.IDENT) {
      eat();
      // dot-access (e.g. Account.Name)
      let name = t.value;
      while (peek().type === TT.DOT) {
        eat();
        const sub = eat(TT.IDENT);
        name += '.' + sub.value;
      }
      // function call
      if (peek().type === TT.LPAREN) {
        eat();
        const args = [];
        while (peek().type !== TT.RPAREN && peek().type !== TT.EOF) {
          args.push(parseExpr());
          if (peek().type === TT.COMMA) eat();
        }
        eat(TT.RPAREN);
        return { type: 'Call', name: name.toUpperCase(), args };
      }
      return { type: 'Field', name: t.merge ? name : name, merge: t.merge };
    }
    throw new Error(`Unexpected token: ${t.type} "${t.value}"`);
  }

  const ast = parseExpr();
  return ast;
}

// ── Evaluator ─────────────────────────────────────────────────────────────────

function evalAST(node, record, priorRecord) {
  const ev = (n) => evalAST(n, record, priorRecord);

  if (node.type === 'Literal') return node.value;

  if (node.type === 'Field') {
    const parts = node.name.split('.');
    let val = record;
    for (const p of parts) {
      if (val == null) return null;
      // case-insensitive field lookup
      const key = Object.keys(val).find(k => k.toLowerCase() === p.toLowerCase());
      val = key !== undefined ? val[key] : null;
    }
    return val ?? null;
  }

  if (node.type === 'Unary') {
    const v = ev(node.expr);
    if (node.op === '-') return -Number(v);
    return v;
  }

  if (node.type === 'BinOp') {
    const L = ev(node.left);
    const R = ev(node.right);
    switch (node.op) {
      case TT.PLUS:      return (typeof L === 'string' || typeof R === 'string') ? String(L ?? '') + String(R ?? '') : Number(L) + Number(R);
      case TT.MINUS:     return Number(L) - Number(R);
      case TT.STAR:      return Number(L) * Number(R);
      case TT.SLASH:     return R === 0 ? null : Number(L) / Number(R);
      case TT.CARET:     return Math.pow(Number(L), Number(R));
      case TT.AMPERSAND: return String(L ?? '') + String(R ?? '');
      case TT.EQ:        return sfEqual(L, R);
      case TT.NEQ:       return !sfEqual(L, R);
      case TT.LT:        return sfCompare(L, R) < 0;
      case TT.GT:        return sfCompare(L, R) > 0;
      case TT.LTE:       return sfCompare(L, R) <= 0;
      case TT.GTE:       return sfCompare(L, R) >= 0;
      default: return null;
    }
  }

  if (node.type === 'Call') {
    const fn = node.name;
    const args = node.args; // lazy eval for IF/AND/OR

    // ── Logical ──────────────────────────────────────────────────────────────
    if (fn === 'IF') {
      const cond = ev(args[0]);
      return cond ? ev(args[1]) : ev(args[2]);
    }
    if (fn === 'AND') return args.every(a => Boolean(ev(a)));
    if (fn === 'OR')  return args.some(a => Boolean(ev(a)));
    if (fn === 'NOT') return !Boolean(ev(args[0]));
    if (fn === 'CASE') {
      const val = ev(args[0]);
      for (let i = 1; i + 1 < args.length; i += 2) {
        if (sfEqual(val, ev(args[i]))) return ev(args[i+1]);
      }
      return args.length % 2 === 0 ? ev(args[args.length-1]) : null;
    }

    // ── Null/Blank checks ────────────────────────────────────────────────────
    if (fn === 'ISBLANK' || fn === 'ISNULL') {
      const v = ev(args[0]);
      return v === null || v === undefined || v === '';
    }
    if (fn === 'ISCHANGED') {
      if (!priorRecord) return false;
      const curr = ev(args[0]);
      const prior = evalAST(args[0], priorRecord, null);
      return !sfEqual(curr, prior);
    }
    if (fn === 'PRIORVALUE') {
      if (!priorRecord) return ev(args[0]);
      return evalAST(args[0], priorRecord, null);
    }
    if (fn === 'ISNEW') return !priorRecord;

    // ── Picklist ─────────────────────────────────────────────────────────────
    if (fn === 'ISPICKVAL') {
      const field = ev(args[0]);
      const val   = ev(args[1]);
      return String(field ?? '').toLowerCase() === String(val ?? '').toLowerCase();
    }
    if (fn === 'INCLUDES') {
      const field = ev(args[0]);
      const val   = ev(args[1]);
      if (Array.isArray(field)) return field.map(f => f.toLowerCase()).includes(String(val).toLowerCase());
      return String(field ?? '').toLowerCase().includes(String(val ?? '').toLowerCase());
    }

    // ── String ───────────────────────────────────────────────────────────────
    if (fn === 'TEXT')        return String(ev(args[0]) ?? '');
    if (fn === 'VALUE')       return parseFloat(ev(args[0])) || 0;
    if (fn === 'LEN')         return String(ev(args[0]) ?? '').length;
    if (fn === 'LOWER')       return String(ev(args[0]) ?? '').toLowerCase();
    if (fn === 'UPPER')       return String(ev(args[0]) ?? '').toUpperCase();
    if (fn === 'TRIM')        return String(ev(args[0]) ?? '').trim();
    if (fn === 'LEFT')        { const s = String(ev(args[0]) ?? ''); const n = Number(ev(args[1])); return s.slice(0, n); }
    if (fn === 'RIGHT')       { const s = String(ev(args[0]) ?? ''); const n = Number(ev(args[1])); return s.slice(-n); }
    if (fn === 'MID')         { const s = String(ev(args[0]) ?? ''); const start = Number(ev(args[1])) - 1; const len = Number(ev(args[2])); return s.slice(start, start + len); }
    if (fn === 'FIND')        { const search = String(ev(args[0]) ?? ''); const str = String(ev(args[1]) ?? ''); const start = args[2] ? Number(ev(args[2])) - 1 : 0; const idx = str.toLowerCase().indexOf(search.toLowerCase(), start); return idx === -1 ? 0 : idx + 1; }
    if (fn === 'CONTAINS')    { return String(ev(args[0]) ?? '').toLowerCase().includes(String(ev(args[1]) ?? '').toLowerCase()); }
    if (fn === 'BEGINS')      { return String(ev(args[0]) ?? '').toLowerCase().startsWith(String(ev(args[1]) ?? '').toLowerCase()); }
    if (fn === 'SUBSTITUTE')  { const s = String(ev(args[0]) ?? ''); const old = String(ev(args[1]) ?? ''); const rep = String(ev(args[2]) ?? ''); return s.split(old).join(rep); }
    if (fn === 'RPAD')        { const s = String(ev(args[0]) ?? ''); const n = Number(ev(args[1])); return s.padEnd(n); }
    if (fn === 'LPAD')        { const s = String(ev(args[0]) ?? ''); const n = Number(ev(args[1])); return s.padStart(n); }

    // ── Math ─────────────────────────────────────────────────────────────────
    if (fn === 'ROUND')   { const v = Number(ev(args[0])); const d = args[1] ? Number(ev(args[1])) : 0; return Math.round(v * Math.pow(10, d)) / Math.pow(10, d); }
    if (fn === 'FLOOR')   return Math.floor(Number(ev(args[0])));
    if (fn === 'CEILING') return Math.ceil(Number(ev(args[0])));
    if (fn === 'ABS')     return Math.abs(Number(ev(args[0])));
    if (fn === 'SQRT')    return Math.sqrt(Number(ev(args[0])));
    if (fn === 'EXP')     return Math.exp(Number(ev(args[0])));
    if (fn === 'LOG')     return Math.log(Number(ev(args[0]))) / Math.log(10);
    if (fn === 'LN')      return Math.log(Number(ev(args[0])));
    if (fn === 'MOD')     { const a = Number(ev(args[0])); const b = Number(ev(args[1])); return a - b * Math.floor(a / b); }
    if (fn === 'MAX')     return Math.max(...args.map(a => Number(ev(a))));
    if (fn === 'MIN')     return Math.min(...args.map(a => Number(ev(a))));

    // ── Date ─────────────────────────────────────────────────────────────────
    if (fn === 'TODAY')     return new Date().toISOString().split('T')[0];
    if (fn === 'NOW')       return new Date().toISOString();
    if (fn === 'DATEVALUE') { const v = ev(args[0]); return typeof v === 'string' ? v.split('T')[0] : v; }
    if (fn === 'YEAR')      { const d = toDate(ev(args[0])); return d ? d.getFullYear() : null; }
    if (fn === 'MONTH')     { const d = toDate(ev(args[0])); return d ? d.getMonth() + 1 : null; }
    if (fn === 'DAY')       { const d = toDate(ev(args[0])); return d ? d.getDate() : null; }
    if (fn === 'ADDMONTHS') { const d = toDate(ev(args[0])); if (!d) return null; d.setMonth(d.getMonth() + Number(ev(args[1]))); return d.toISOString().split('T')[0]; }
    if (fn === 'DATE')      { const y = Number(ev(args[0])); const m = Number(ev(args[1])); const d = Number(ev(args[2])); return new Date(y, m-1, d).toISOString().split('T')[0]; }

    // ── Misc ─────────────────────────────────────────────────────────────────
    if (fn === 'BLANKVALUE') { const v = ev(args[0]); return (v === null || v === undefined || v === '') ? ev(args[1]) : v; }
    if (fn === 'NULLVALUE')  { const v = ev(args[0]); return v === null || v === undefined ? ev(args[1]) : v; }
    if (fn === 'BR')         return '\n';
    if (fn === 'IMAGE')      return `[IMG: ${ev(args[0])}]`;
    if (fn === 'HYPERLINK')  return `[LINK: ${ev(args[1])}]`;

    throw new Error(`Unknown function: ${fn}`);
  }

  throw new Error(`Unknown node type: ${node.type}`);
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function sfEqual(a, b) {
  if (a === null || a === undefined) return b === null || b === undefined || b === '';
  if (b === null || b === undefined) return a === null || a === undefined || a === '';
  if (typeof a === 'string' && typeof b === 'string') return a.toLowerCase() === b.toLowerCase();
  return a === b;
}

function sfCompare(a, b) {
  // date strings compare lexicographically which works for ISO dates
  if (typeof a === 'string' && typeof b === 'string') return a.localeCompare(b);
  return Number(a) - Number(b);
}

function toDate(v) {
  if (!v) return null;
  if (v instanceof Date) return v;
  const d = new Date(v);
  return isNaN(d) ? null : d;
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * evaluate(formula, record, priorRecord?)
 * @param {string} formula  - Salesforce formula string
 * @param {object} record   - field values e.g. { Amount: 50000, StageName: 'Closed Won' }
 * @param {object} [prior]  - prior field values for PRIORVALUE/ISCHANGED
 * @returns {{ result: any, error: string|null }}
 */
export function evaluate(formula, record, prior = null) {
  try {
    const tokens = tokenize(formula);
    const ast    = parse(tokens);
    const result = evalAST(ast, record, prior);
    return { result, error: null };
  } catch (e) {
    return { result: null, error: e.message };
  }
}

/**
 * evaluateValidationRule(formula, record, priorRecord?)
 * Returns { fires: boolean, error: string|null }
 * "fires" = true means the validation rule would BLOCK the save
 */
export function evaluateValidationRule(formula, record, prior = null) {
  const { result, error } = evaluate(formula, record, prior);
  return { fires: Boolean(result), error };
}

/**
 * getFormulaFunctions() - returns categorised list for the UI function picker
 */
export function getFormulaFunctions() {
  return [
    { category: 'Logical', fns: [
      { name: 'IF', syntax: 'IF(condition, value_if_true, value_if_false)', desc: 'Returns one of two values based on a condition' },
      { name: 'AND', syntax: 'AND(condition1, condition2, ...)', desc: 'Returns TRUE if all conditions are true' },
      { name: 'OR',  syntax: 'OR(condition1, condition2, ...)',  desc: 'Returns TRUE if any condition is true' },
      { name: 'NOT', syntax: 'NOT(condition)', desc: 'Reverses a boolean value' },
      { name: 'CASE', syntax: 'CASE(expr, val1, result1, ..., else)', desc: 'Like a switch statement' },
    ]},
    { category: 'Null & Blank', fns: [
      { name: 'ISBLANK',    syntax: 'ISBLANK(field)',          desc: 'TRUE if the field has no value' },
      { name: 'ISNULL',     syntax: 'ISNULL(field)',           desc: 'TRUE if the field is null' },
      { name: 'BLANKVALUE', syntax: 'BLANKVALUE(field, val)',  desc: 'Returns val if field is blank' },
      { name: 'NULLVALUE',  syntax: 'NULLVALUE(field, val)',   desc: 'Returns val if field is null' },
    ]},
    { category: 'Picklist', fns: [
      { name: 'ISPICKVAL', syntax: 'ISPICKVAL(field, "value")', desc: 'TRUE if picklist field equals value' },
      { name: 'INCLUDES',  syntax: 'INCLUDES(field, "value")',  desc: 'TRUE if multi-select includes value' },
    ]},
    { category: 'Text', fns: [
      { name: 'TEXT',       syntax: 'TEXT(value)',                desc: 'Converts to text' },
      { name: 'VALUE',      syntax: 'VALUE(text)',                desc: 'Converts text to number' },
      { name: 'LEN',        syntax: 'LEN(text)',                  desc: 'Length of text' },
      { name: 'LEFT',       syntax: 'LEFT(text, n)',              desc: 'First n characters' },
      { name: 'RIGHT',      syntax: 'RIGHT(text, n)',             desc: 'Last n characters' },
      { name: 'MID',        syntax: 'MID(text, start, length)',   desc: 'Characters from position' },
      { name: 'FIND',       syntax: 'FIND(search, text, start?)', desc: 'Position of search string' },
      { name: 'CONTAINS',   syntax: 'CONTAINS(text, search)',     desc: 'TRUE if text contains search' },
      { name: 'BEGINS',     syntax: 'BEGINS(text, search)',       desc: 'TRUE if text starts with search' },
      { name: 'LOWER',      syntax: 'LOWER(text)',                desc: 'Lowercase' },
      { name: 'UPPER',      syntax: 'UPPER(text)',                desc: 'Uppercase' },
      { name: 'TRIM',       syntax: 'TRIM(text)',                 desc: 'Remove leading/trailing spaces' },
      { name: 'SUBSTITUTE', syntax: 'SUBSTITUTE(text, old, new)', desc: 'Replace occurrences' },
    ]},
    { category: 'Math', fns: [
      { name: 'ROUND',   syntax: 'ROUND(number, digits)',   desc: 'Round to decimal places' },
      { name: 'FLOOR',   syntax: 'FLOOR(number)',           desc: 'Round down' },
      { name: 'CEILING', syntax: 'CEILING(number)',         desc: 'Round up' },
      { name: 'ABS',     syntax: 'ABS(number)',             desc: 'Absolute value' },
      { name: 'MOD',     syntax: 'MOD(number, divisor)',    desc: 'Remainder after division' },
      { name: 'MAX',     syntax: 'MAX(n1, n2, ...)',        desc: 'Largest value' },
      { name: 'MIN',     syntax: 'MIN(n1, n2, ...)',        desc: 'Smallest value' },
      { name: 'SQRT',    syntax: 'SQRT(number)',            desc: 'Square root' },
    ]},
    { category: 'Date', fns: [
      { name: 'TODAY',     syntax: 'TODAY()',                     desc: 'Current date' },
      { name: 'NOW',       syntax: 'NOW()',                       desc: 'Current date and time' },
      { name: 'DATEVALUE', syntax: 'DATEVALUE(datetime)',         desc: 'Extract date from datetime' },
      { name: 'DATE',      syntax: 'DATE(year, month, day)',      desc: 'Create a date' },
      { name: 'YEAR',      syntax: 'YEAR(date)',                  desc: 'Year from date' },
      { name: 'MONTH',     syntax: 'MONTH(date)',                 desc: 'Month from date' },
      { name: 'DAY',       syntax: 'DAY(date)',                   desc: 'Day from date' },
      { name: 'ADDMONTHS', syntax: 'ADDMONTHS(date, n)',          desc: 'Add months to date' },
    ]},
    { category: 'Record', fns: [
      { name: 'PRIORVALUE', syntax: 'PRIORVALUE(field)',  desc: 'Previous value before edit (validation rules)' },
      { name: 'ISCHANGED',  syntax: 'ISCHANGED(field)',   desc: 'TRUE if field changed on update' },
      { name: 'ISNEW',      syntax: 'ISNEW()',            desc: 'TRUE if record is being created' },
    ]},
  ];
}